import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, ListGroup, Modal } from 'react-bootstrap';
import { fetchStudyPipeline, savePatientNote } from '../api';
import { SURGERIES } from './MedicationCheckboxForm';
import { listRowKey } from '../utils/effectiveAppointmentType';
import { demoChartsUseDatabase, onDemoChartSaved } from '../studies/studyChartStore';
import { allStagesComplete, chartSurgeryDate, parseSurgeryDay, pipelineForStudy, stageLists } from '../studies/studyPipeline';
import LoadingSpinner from './LoadingSpinner';

function surgeryLabel(token) {
  return String(token || '').replace(/_/g, ' ');
}

function formatPipelineDate(value) {
  const date = parseSurgeryDay(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function completedPatients(stages, patients) {
  return (patients || []).filter((patient) => allStagesComplete(stages, patient));
}

function sectionGroups(section) {
  if (section.rows) return section.rows.length ? [{ heading: '', rows: section.rows }] : [];
  return [
    { heading: 'Action Required', rows: section.action },
    { heading: 'Upcoming', rows: section.upcoming },
  ].filter((group) => group.rows.length);
}

export default function StudyPipelineList({
  study,
  searchTerm = '',
  blurPatientNames = false,
  selectedPatientId = null,
  onSelect,
  onPatients,
  enrollmentPatch = null,
  reloadToken = 0,
}) {
  const pipeline = pipelineForStudy(study);
  const [patients, setPatients] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scheduleFor, setScheduleFor] = useState(null);
  const [surgeryKey, setSurgeryKey] = useState('');
  const [surgeryDate, setSurgeryDate] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const requestSeq = useRef(0);
  const rowPatch = useRef(new Map());

  const withPatches = (rows, started) => rows.map((patient) => {
    const patch = rowPatch.current.get(String(patient.id));
    if (!patch || patch.at < started) return patient;
    return {
      ...patient,
      ...(patch.phases ? { phases: patch.phases } : {}),
      ...(patch.surgeryDate ? { surgery_date: patch.surgeryDate, surgeryDate: patch.surgeryDate } : {}),
      ...(patch.declined != null ? { declined: patch.declined } : {}),
    };
  });

  const load = useCallback(async () => {
    if (!pipeline) return;
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    const started = Date.now();
    if (!demoChartsUseDatabase()) {
      setPatients([]);
      setError('The study list is available on the demo and live databases.');
      setLoading(false);
      return;
    }
    setError('');
    try {
      const result = await fetchStudyPipeline(pipeline.id);
      if (seq !== requestSeq.current) return;
      const rows = Array.isArray(result?.patients) ? result.patients : [];
      setPatients(withPatches(rows, started));
    } catch (loadError) {
      if (seq !== requestSeq.current) return;
      setPatients([]);
      setError(loadError?.message || 'The study list did not load.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [pipeline]);

  const applySavedChart = useCallback((patientId, phases, surgeryDate) => {
    if (!patientId) return;
    const nextPhases = phases && typeof phases === 'object' ? phases : null;
    const nextDate = String(surgeryDate || '').trim();
    if (!nextPhases && !nextDate) return;
    const key = String(patientId);
    rowPatch.current.set(key, {
      ...(rowPatch.current.get(key) || {}),
      ...(nextPhases ? { phases: nextPhases } : {}),
      ...(nextDate ? { surgeryDate: nextDate } : {}),
      at: Date.now(),
    });
    setPatients((rows) => rows.map((row) => (
      String(row.id) === key
        ? {
          ...row,
          ...(nextPhases ? { phases: nextPhases } : {}),
          ...(nextDate ? { surgery_date: nextDate, surgeryDate: nextDate } : {}),
        }
        : row
    )));
  }, []);

  useEffect(() => {
    setCollapsed({});
  }, [pipeline]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, reloadToken]);

  useEffect(() => onDemoChartSaved((patientId, detail) => {
    if (!pipeline || (detail?.study && detail.study !== pipeline.id)) return;
    applySavedChart(patientId, detail?.state?.phases, chartSurgeryDate(detail?.state));
  }), [applySavedChart, pipeline]);

  useEffect(() => {
    onPatients?.(patients);
  }, [onPatients, patients]);

  useEffect(() => {
    if (!enrollmentPatch?.patientId || enrollmentPatch.at == null) return;
    const key = String(enrollmentPatch.patientId);
    rowPatch.current.set(key, {
      ...(rowPatch.current.get(key) || {}),
      declined: Boolean(enrollmentPatch.declined),
      at: enrollmentPatch.at,
    });
    setPatients((rows) => rows.map((row) => (
      String(row.id) === key ? { ...row, declined: Boolean(enrollmentPatch.declined) } : row
    )));
  }, [enrollmentPatch]);

  if (!pipeline) return null;
  const needle = searchTerm.trim().toLowerCase();
  const visible = patients.filter((patient) => (
    !needle || String(patient.name || '').toLowerCase().includes(needle)
  ));
  const sections = [
    ...pipeline.stages.map((stage) => {
      const lists = stageLists(stage, visible);
      return {
        id: stage.id,
        label: stage.label.toUpperCase(),
        action: lists.action,
        upcoming: lists.upcoming,
        actionCount: lists.action.length,
        showPill: true,
        muted: false,
      };
    }),
    {
      id: 'denied-enrollment',
      label: 'DENIED ENROLLMENT',
      rows: visible.filter((patient) => patient.declined),
      action: [],
      upcoming: [],
      actionCount: 0,
      showPill: false,
      muted: true,
    },
    {
      id: 'completed',
      label: 'COMPLETED',
      rows: completedPatients(pipeline.stages, visible),
      action: [],
      upcoming: [],
      actionCount: 0,
      showPill: false,
      muted: true,
    },
  ];
  const nameStyle = blurPatientNames
    ? { filter: 'blur(7px)', userSelect: 'none', WebkitUserSelect: 'none' }
    : undefined;

  const askForSurgery = (patient) => {
    setScheduleFor(patient);
    setSurgeryKey('');
    setSurgeryDate('');
    setScheduleError('');
  };

  const closeSchedule = () => {
    if (scheduleBusy) return;
    setScheduleFor(null);
    setScheduleError('');
  };

  const submitSchedule = async () => {
    if (!scheduleFor || !surgeryKey || !surgeryDate) return;
    if (!scheduleFor.appointment_id) {
      setScheduleError('This patient has no appointment to attach a surgery to.');
      return;
    }
    setScheduleBusy(true);
    setScheduleError('');
    try {
      await savePatientNote(scheduleFor.id, '', [], [], scheduleFor.appointment_id, {
        additionalSurgery: {
          scheduled_surgery: { procedure: [surgeryKey], date: surgeryDate },
          valid: true,
        },
      });
      setScheduleFor(null);
      await load();
    } catch (saveError) {
      setScheduleError(saveError?.message || 'Could not schedule this surgery.');
    } finally {
      setScheduleBusy(false);
    }
  };

  const toggleSection = (stageId) => {
    setCollapsed((current) => ({ ...current, [stageId]: !current[stageId] }));
  };

  return (
    <div>
      {error && <div className="text-danger small px-3 mb-2">{error}</div>}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div>
          {sections.map((section) => {
            const closed = Boolean(collapsed[section.id]);
            const groups = sectionGroups(section);
            return (
              <div key={section.id} className="mb-3">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none w-100 text-start px-3 py-0 mb-2"
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={!closed}
                >
                  <h6 className="text-muted mb-0 d-flex justify-content-between align-items-center">
                    <span className="d-flex align-items-center">
                      {section.label}
                      {section.showPill && (
                        <span
                          className="badge rounded-pill ms-2"
                          style={section.actionCount
                            ? { backgroundColor: '#dc3545', color: '#fff', fontSize: '0.7rem' }
                            : { backgroundColor: '#e9ecef', color: '#6c757d', fontSize: '0.7rem' }}
                        >
                          {section.actionCount}
                        </span>
                      )}
                    </span>
                    <span aria-hidden="true">{closed ? '▸' : '▾'}</span>
                  </h6>
                </button>
                {!closed && groups.length > 0 && (
                  <div>
                    {groups.map((group) => (
                      <div key={group.heading || 'rows'} className="mb-2">
                        {group.heading ? (
                          <div className="px-3 text-muted small fw-semibold mb-1">{group.heading}</div>
                        ) : null}
                        <ListGroup>
                          {group.rows.map((patient) => {
                            const surgeryWhen = patient.surgery_date || patient.surgeryDate;
                            return (
                              <ListGroup.Item
                                key={`${section.id}:${group.heading}:${listRowKey(patient)}`}
                                active={selectedPatientId === listRowKey(patient)}
                                onClick={() => {
                                  onSelect?.(patient.id, patient.appointment_id);
                                  if (section.id === 'preop' && !surgeryWhen) askForSurgery(patient);
                                }}
                                action
                                className="d-flex align-items-center justify-content-between"
                                style={section.muted ? { backgroundColor: '#f8f9fa', opacity: 0.8 } : undefined}
                              >
                                <div className="d-flex align-items-center">
                                  <span style={nameStyle}>{patient.name}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  {surgeryWhen ? (
                                    <span className="badge rounded-pill" style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-dark)', fontSize: '0.75rem' }}>
                                      {formatPipelineDate(surgeryWhen)}
                                    </span>
                                  ) : null}
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Modal
        show={Boolean(scheduleFor)}
        onHide={closeSchedule}
        centered
        animation={false}
      >
        <Modal.Header closeButton={!scheduleBusy}>
          <Modal.Title>Schedule a surgery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            <span style={nameStyle}>{scheduleFor?.name || 'This patient'}</span>
            {' '}is in pre-op and does not have a surgery scheduled.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Surgery</Form.Label>
            <Form.Select
              value={surgeryKey}
              onChange={(event) => setSurgeryKey(event.target.value)}
              aria-label="Surgery"
              disabled={scheduleBusy}
            >
              <option value="">Select a surgery</option>
              {SURGERIES.map((token) => (
                <option key={token} value={token}>{surgeryLabel(token)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Surgery date</Form.Label>
            <Form.Control
              type="date"
              value={surgeryDate}
              onChange={(event) => setSurgeryDate(event.target.value)}
              aria-label="Surgery date"
              disabled={scheduleBusy}
            />
          </Form.Group>
          {scheduleError && <p className="text-danger small mt-3 mb-0">{scheduleError}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeSchedule} disabled={scheduleBusy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitSchedule}
            disabled={scheduleBusy || !surgeryKey || !surgeryDate}
          >
            {scheduleBusy ? 'Saving…' : 'Save surgery'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
